/**
 * EnvelopeReviewPage
 *
 * Pre-send confirmation step. Owner double-checks recipients, fields, and
 * expiration before flipping the envelope to `sent`. After this point the
 * envelope is read-only — switching back to a draft would invalidate every
 * outstanding signing token, which is a deliberate path we don't ship in v1.
 */

import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { HolderSwitcher, useHolderSwitcher } from '@/components/HolderSwitcher';
import { ArcheHeader } from '../../../components/ArcheHeader';
import { useEnvelope, useSendEnvelope, useRecipients } from '../hooks';
import { useSelectedHolder } from '../hooks/useSelectedHolder';

export function EnvelopeReviewPage() {
  const { envelopeId } = useParams<{ envelopeId: string }>();
  const navigate = useNavigate();
  const { envelope, loading } = useEnvelope(envelopeId);
  const { recipients } = useRecipients(envelopeId);
  const send = useSendEnvelope(envelopeId ?? '');

  // Active workspace chip in the header. Distinct from the envelope's own
  // holder (fixed at create-time) — the chip reflects the user's preference
  // for OTHER work and persists across arches.
  const { holderId: activeHolderId, setHolderId: setActiveHolderId } = useSelectedHolder();

  // Friendly display name for the envelope's holder — used in the
  // "Sending from" callout below. The envelope holder is immutable, so we
  // look it up via the same workspace list the chip uses; if the user can
  // no longer see this holder (e.g. revoked from the org) we fall back to
  // the raw holder id.
  const switcher = useHolderSwitcher();
  const envelopeWorkspaceName =
    switcher.options.find((o) => o.holderId === envelope?.holderId)?.displayName ?? null;

  if (!envelopeId) return null;

  if (loading || !envelope) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_document_sign" title="Document Sign">
          <HolderSwitcher selectedHolderId={activeHolderId} onChange={setActiveHolderId} />
        </ArcheHeader>
        <div className="max-w-3xl mx-auto p-6 w-full space-y-3">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  const sourceFiles = envelope.files.filter((f) => f.kind === 'source');
  const fieldsByRecipient: Record<string, number> = {};
  for (const f of envelope.fields) {
    fieldsByRecipient[f.recipientId] = (fieldsByRecipient[f.recipientId] ?? 0) + 1;
  }

  async function handleSend() {
    try {
      await send.mutateAsync();
      // Confirmation screen — the builder URL is the wrong surface for a
      // non-draft envelope (it's the edit view) and a recipient who lands
      // on the detail view without seeing a confirmation can wonder
      // whether anything actually happened.
      navigate(`/a/arche_document_sign/envelope/${envelopeId}/sent`);
    } catch {
      // Error surfaced via send.error below
    }
  }

  const canSend = envelope.status === 'draft';

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId="arche_document_sign" title="Document Sign">
        <HolderSwitcher selectedHolderId={activeHolderId} onChange={setActiveHolderId} />
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/a/arche_document_sign/envelope/${envelopeId}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to builder
        </Button>
      </ArcheHeader>
      <div className="max-w-3xl mx-auto p-6 w-full space-y-6">
        <div>
          <h1 className="text-xl font-semibold">Send "{envelope.title}"</h1>
          <p className="text-sm text-muted-foreground">
            Double-check the recipients and fields below. Once sent, you can void the envelope but
            cannot edit it.
          </p>
        </div>

        {/*
         * "Sending from" callout. The most important workspace check happens
         * here — Send is the irreversible action. If the user accidentally
         * built this envelope under the wrong workspace, this is their last
         * chance to notice before tokens are minted and emails go out.
         * Recovery from here is "Back to builder" → void the draft → start
         * over under the right workspace (the global chip).
         */}
        <section
          className="border-l-4 border-primary bg-primary/5 rounded-r-md p-4"
          aria-label="Workspace context"
        >
          <p className="text-sm">
            <span className="text-muted-foreground">Sending from: </span>
            <strong>{envelopeWorkspaceName ?? envelope.holderId}</strong>
            <span className="text-muted-foreground">
              {' '}
              · {recipients.length} {recipients.length === 1 ? 'recipient' : 'recipients'}
            </span>
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Recipients will see this as coming from this workspace. The workspace can't be changed
            after sending.
          </p>
        </section>

        <section className="border rounded-md p-4 space-y-2">
          <h2 className="text-sm font-semibold">Documents</h2>
          {sourceFiles.length === 0 ? (
            <p className="text-sm text-destructive">No source documents attached.</p>
          ) : (
            <ul className="text-sm list-disc pl-4">
              {sourceFiles.map((f) => (
                <li key={f.fileId}>
                  {f.fileName}
                  {f.pageCount !== null && (
                    <span className="text-muted-foreground"> ({f.pageCount} pages)</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="border rounded-md p-4 space-y-2">
          <h2 className="text-sm font-semibold">
            Recipients ({recipients.length}, {envelope.signingOrder} order)
          </h2>
          {recipients.length === 0 ? (
            <p className="text-sm text-destructive">No recipients added.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {recipients.map((r) => (
                <li key={r.recipientId} className="flex justify-between gap-3">
                  <span>
                    {envelope.signingOrder === 'sequential' && (
                      <span className="text-xs text-muted-foreground mr-2">
                        #{r.routingOrder + 1}
                      </span>
                    )}
                    <strong>{r.name ?? r.email ?? 'Unnamed'}</strong>
                    {r.email && r.name && (
                      <span className="text-muted-foreground"> ({r.email})</span>
                    )}
                    <span className="text-xs text-muted-foreground ml-2">{r.role}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {fieldsByRecipient[r.recipientId] ?? 0} fields
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="border rounded-md p-4 space-y-2">
          <h2 className="text-sm font-semibold">Expiration</h2>
          <p className="text-sm">
            Each signing link expires 30 days after sending. Recipients who haven't completed by
            then will need a new link.
          </p>
        </section>

        {send.error && (
          <p className="text-sm text-destructive">
            {send.error instanceof Error ? send.error.message : 'Failed to send'}
          </p>
        )}

        <div className="flex gap-2">
          <Button onClick={handleSend} disabled={!canSend || send.isPending}>
            <Send className="h-4 w-4 mr-2" />
            {send.isPending ? 'Sending…' : 'Send for signature'}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/a/arche_document_sign/envelope/${envelopeId}`)}
            disabled={send.isPending}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
